import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/shadcn/form.tsx';
import { Input } from '@/components/shadcn/input.tsx';
import { Button } from '@/components/shadcn/button.tsx';
import {
  Card,
  CardHeader,
  CardDescription,
  CardContent,
} from '@/components/shadcn/card.tsx';
import * as Typography from '@/components/shadcn/typography';
import { LoadingSpinner } from '@/components/shadcn/loader.tsx';

const formSchema = z.object({
  email: z.string().email(),
  area: z.string().min(1, 'Please enter a location'),
  description: z.string().min(1, 'Please enter a description'),
});

export default function RedactionRequest() {
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      area: '',
      description: '',
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      setIsLoading(true);
      await fetch(
        'https://hv3h5e49c8.execute-api.us-west-2.amazonaws.com/prod/privacyContactUs',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        },
      );

      form.reset();
      setSuccessMessage(
        'Your request has been received and we will contact you shortly.',
      );
      setIsLoading(false);
    } catch (error) {
      console.error('Error submitting data:', error);
      alert('Error submitting data. Please try again later.');
    }
  }
  return (
    <div className="flex flex-col gap-12">
      <Card className="border border-neutral-700 bg-neutral-900 sm:w-3/4">
        <CardHeader>
          <Typography.H4>Redaction Request</Typography.H4>
          <CardDescription>
            Hivemapper can assist with blurring or deletion of images collected
            by the network that you consider sensitive or your personal
            information (e.g. your face, body, vehicle, license plate or home).
            If you would like to request this, please fill out this form.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input placeholder="email@address.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="area"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Street, City, State, Zip Code, Country"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Provide additional details"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-center">
                {successMessage ? (
                  <p className="text-veridianSuccess-700 py-2 text-center">
                    {successMessage}
                  </p>
                ) : isLoading ? (
                  <LoadingSpinner />
                ) : (
                  <Button type="submit">Submit Request</Button>
                )}
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
